/**
 * Button - Primary action component
 *
 * Usage:
 * <Button>Click me</Button>
 * <Button variant="secondary" size="sm">Cancel</Button>
 * <Button variant="danger" loading>Deleting...</Button>
 * <Button leftIcon={<PlusIcon />}>Add Item</Button>
 */

import { forwardRef } from 'react';

import { Spinner } from '../feedback';
import { cn } from '../utils';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** Button variant */
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger';
  /** Button size */
  size?: 'sm' | 'md' | 'lg';
  /** Show loading state */
  loading?: boolean;
  /** Icon on the left */
  leftIcon?: React.ReactNode;
  /** Icon on the right */
  rightIcon?: React.ReactNode;
}

const variantStyles = {
  primary: 'bg-blue-600 text-white hover:bg-blue-500 focus-visible:ring-blue-500',
  secondary:
    'bg-gray-200 text-gray-700 hover:bg-gray-300 hover:text-gray-900 dark:bg-zinc-800 dark:text-zinc-300 dark:hover:bg-zinc-700 dark:hover:text-white focus-visible:ring-gray-500 dark:focus-visible:ring-zinc-500',
  outline:
    'border border-gray-200 bg-white text-gray-900 hover:border-gray-300 hover:bg-gray-50 dark:border-white/[0.06] dark:bg-[#18181b] dark:text-white dark:hover:border-white/[0.1] dark:hover:bg-[#1f1f23] focus-visible:ring-gray-500 dark:focus-visible:ring-zinc-500',
  ghost:
    'bg-transparent text-gray-600 hover:bg-gray-100 hover:text-gray-900 dark:text-zinc-300 dark:hover:bg-white/[0.04] dark:hover:text-white focus-visible:ring-gray-500 dark:focus-visible:ring-zinc-500',
  danger: 'bg-red-600 text-white hover:bg-red-500 focus-visible:ring-red-500',
};

const sizeStyles = {
  sm: 'px-3 py-2 text-sm gap-1.5 sm:py-1.5',
  md: 'px-4 py-2.5 text-sm gap-2 sm:py-2',
  lg: 'px-5 py-3 text-base gap-2 sm:py-2.5',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      type = 'button',
      variant = 'primary',
      size = 'md',
      loading,
      leftIcon,
      rightIcon,
      disabled,
      className,
      children,
      ...props
    },
    ref
  ) => {
    const isDisabled = disabled || loading;

    return (
      <button
        ref={ref}
        type={type}
        disabled={isDisabled}
        className={cn(
          'inline-flex cursor-pointer items-center justify-center rounded-lg font-medium transition-colors',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:focus-visible:ring-offset-[#09090b]',
          variantStyles[variant],
          sizeStyles[size],
          isDisabled && 'cursor-not-allowed opacity-50',
          className
        )}
        {...props}
      >
        {loading ? <Spinner size="sm" className="text-current" /> : leftIcon}
        {children}
        {rightIcon && !loading && rightIcon}
      </button>
    );
  }
);

Button.displayName = 'Button';
